import React from 'react';
import { cn } from '../../utils/cn'
import type { NodeStatus, NodeInfo, AlertSeverity } from '../../types'

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'status' | 'severity' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'
  /** A console display status, or socket-cloud NodeStatus (healthy, degraded,
   * offline) as the node list caches it; healthy takes the online style. */
  status?: NodeStatus | NodeInfo['status']
  severity?: AlertSeverity
  size?: 'sm' | 'md' | 'lg'
  pulse?: boolean
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', status, severity, size = 'md', pulse = false, children, ...props }, ref) => {
    let badgeClasses = 'inline-flex items-center rounded-full font-medium';

    // Size classes
    switch (size) {
      case 'sm':
        badgeClasses += ' px-2 py-0.5 text-xs';
        break;
      case 'lg':
        badgeClasses += ' px-3 py-1 text-sm';
        break;
      default:
        badgeClasses += ' px-2.5 py-0.5 text-xs';
    }

    // Variant-based styling
    switch (variant) {
      case 'primary':
        badgeClasses += ' bg-primary-600/20 text-primary-400 border border-primary-600/30';
        break;
      case 'secondary':
        badgeClasses += ' bg-secondary-600/20 text-secondary-400 border border-secondary-600/30';
        break;
      case 'success':
        badgeClasses += ' bg-status-online/20 text-status-online border border-status-online/30';
        break;
      case 'warning':
        badgeClasses += ' bg-warning-600/20 text-warning-400 border border-warning-600/30';
        break;
      case 'danger':
        badgeClasses += ' bg-danger-600/20 text-danger-400 border border-danger-600/30';
        break;
      case 'status':
        if (status) {
          switch (status) {
            case 'online':
            case 'healthy':
              badgeClasses += ' status-online';
              break;
            case 'offline':
              badgeClasses += ' status-offline';
              break;
            case 'degraded':
              badgeClasses += ' status-degraded';
              break;
            case 'maintenance':
              badgeClasses += ' status-maintenance';
              break;
            case 'heartbeat_expired':
              badgeClasses += ' status-heartbeat-expired';
              break;
          }
        }
        break;
      case 'severity':
        if (severity) {
          switch (severity) {
            case 'info':
              badgeClasses += ' bg-blue-500/10 text-blue-400 border border-blue-500/20';
              break;
            case 'warning':
              badgeClasses += ' bg-yellow-500/10 text-yellow-400 border border-yellow-500/20';
              break;
            case 'error':
              badgeClasses += ' bg-red-500/10 text-red-400 border border-red-500/20';
              break;
            case 'critical':
              badgeClasses += ' bg-red-600/20 text-red-300 border border-red-600/30';
              break;
            default:
              badgeClasses += ' bg-secondary-700 text-secondary-200 border border-secondary-600';
          }
        }
        break;
      default:
        badgeClasses += ' bg-secondary-700 text-secondary-200 border border-secondary-600';
    }

    // Pulse animation
    if (pulse) {
      badgeClasses += ' animate-pulse';
    }

    return (
      <span
        ref={ref}
        className={cn(badgeClasses, className)}
        {...props}
      >
        {children}
      </span>
    );
  },
);

Badge.displayName = 'Badge';

/** Display label per node status; the raw enum value carries an underscore. */
export const statusLabels: Record<NodeStatus | NodeInfo['status'], string> = {
  online: 'Online',
  healthy: 'Healthy',
  offline: 'Offline',
  degraded: 'Degraded',
  maintenance: 'Maintenance',
  heartbeat_expired: 'Heartbeat expired',
};

/**
 * Status indicator with dot
 */
export interface StatusIndicatorProps {
  status: NodeStatus | NodeInfo['status']
  label?: string
  showLabel?: boolean
  size?: 'sm' | 'md' | 'lg'
  pulse?: boolean
}

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  label,
  showLabel = true,
  size = 'md',
  pulse = false,
}) => {
  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3', 
    lg: 'w-4 h-4',
  };

  const statusColors: Record<NodeStatus | NodeInfo['status'], string> = {
    online: 'bg-status-online',
    healthy: 'bg-status-online',
    offline: 'bg-status-offline',
    degraded: 'bg-status-degraded',
    maintenance: 'bg-status-maintenance',
    heartbeat_expired: 'bg-status-heartbeat-expired',
  };

  return (
    <div className="inline-flex items-center gap-2">
      <div
        className={cn(
          'rounded-full',
          sizeClasses[size],
          statusColors[status],
          pulse && 'animate-pulse',
        )}
      />
      {showLabel && (
        <span className="text-sm text-console-text capitalize">
          {label || statusLabels[status]}
        </span>
      )}
    </div>
  );
};